/*
 * Copyright (C) 2018 Spanning Cloud Apps. LLC  All rights reserved.
 */
import * as _l from 'lodash';

import { ValidationUtils } from 'spanalog/lib/utils/ValidationUtils';
import './dropdown.tpl.html';

(function () {
  // These are the selection types supported in the dropdown
  var SELECT_TYPE_SINGLE = 'single';
  var SELECT_TYPE_MULTI = 'multi';

  /**
   * This directive displays a dropdown menu with a search field. The dropdown supports single selection as clickable
   * text or multi-selection as checkboxes.
   */
  angular
    .module('shared.dropdown', ['services.util'])
    .directive('sbsfDropdown', directive)
    .controller('DropdownCtrl', controller);

  /**
   * Defines the sbsf-dropdown directive.
   *
   * @returns {Object} - The directive settings
   */
  function directive() {
    return {
      controller: 'DropdownCtrl',
      templateUrl: 'shared/dropdown/dropdown.tpl.html',
      restrict: 'E',
      scope: {
        // The text to display in the menu button where there is no selected value
        defaultText: '=',
        // Whether the dropdown should be disabled
        disabled: '=',
        // The list of values that should be grayed out in the dropdown
        disabledValues: '=',
        // Custom comparator function to compare values which needs to be disabled
        disableValuesComparator: '=',
        // Set true to hide the search field
        hideSearch: '=',
        // Optional property to use for a label if the values are objects
        labelProp: '@',
        // Optional property to map the values to labels if different display text is desired. Should be 1-to-1 with
        // the values.
        labels: '=',
        // The function to be called when a value is selected. The function is passed the value.
        onValueSelected: '&',
        // The function to be called when a value is unselected. The function is passed the value.
        // This only happens if selectType === 'multi'
        onValueUnselected: '&',
        // The placeholder to display in the search field
        searchPlaceholder: '=',
        // The currently selected value for single select
        selectedValue: '=',
        // The currently selected values for multi select
        selectedValues: '=',
        // 'single' or 'multi'
        selectType: '@',
        // The values to display in the dropdown menu
        values: '=',
        // highlight active item
        highlightActive: '='
      }
    };
  }

  controller.$inject = ['$q', '$scope', 'UtilService'];

  /**
   * The controller for the directive.
   */
  function controller($q, $scope, UtilService) {
    // Publish scope functions
    $scope.getValueLabel = getValueLabel;
    $scope.hasSelection = hasSelection;
    $scope.isMultiSelect = isMultiSelect;
    $scope.isValueDisabled = isValueDisabled;
    $scope.isValueSelected = isValueSelected;
    $scope.preventClose = preventClose;
    $scope.valueClicked = valueClicked;

    // Exposed for testing
    $scope.buildSelectedText = buildSelectedText;

    validateSettings();

    // The search text is defined this way so the input field in its ng-if child scope can modify the text
    $scope.search = { text: undefined };

    $scope.$watch(
      function () {
        return $scope.selectedValue;
      },
      function () {
        buildSelectedText();
      }
    );

    $scope.$watchCollection(
      function () {
        return $scope.selectedValues;
      },
      function () {
        buildSelectedText();
      }
    );

    /**
     * Builds the text to be displayed on the menu button.
     *
     * @return {string} the text to be displayed.
     */
    function buildSelectedText() {
      if (isMultiSelect()) {
        var labels = _.map($scope.selectedValues, function (value) {
          return getValueLabel(value);
        });
        labels = _.sortBy(labels, function (label) {
          return label;
        });
        $scope.selectedText = labels.join(',');
      } else {
        $scope.selectedText = getValueLabel($scope.selectedValue);
      }

      // Fall back to the default if there's no value to display
      if (!$scope.selectedText) {
        $scope.selectedText = $scope.defaultText;
      }
    }

    /**
     * Returns the index of a value in a values array.
     * TODO Use _.findIndex if we upgrade underscore or lodash
     *
     * @param {array} values - the list of values to be checked.
     * @param {object|string} value - the value being sought.
     * @return {number} - the index of the found value, or -1 if the value was not found.
     */
    function findIndex(values, value) {
      var index = -1;
      var found = _.find(values, function (selectedValue) {
        index++;
        var selectedValueLabel = getValueLabel(selectedValue);
        var valueLabel = getValueLabel(value);
        return selectedValueLabel === valueLabel;
      });

      if (!found) {
        index = -1;
      }

      return index;
    }

    /**
     * Returns the label to be used for a value object. The default is to use the value object itself.
     *
     * @param {object|string} value - the value object
     */
    function getValueLabel(value) {
      if (value && $scope.labelProp) {
        return value[$scope.labelProp];
      } else if (value && $scope.labels) {
        const index = _l.indexOf($scope.values, value);
        return $scope.labels[index];
      } else {
        return value;
      }
    }

    /**
     * Determines whether something is selected.
     *
     * @returns {boolean} true if something is selected, false otherwise.
     */
    function hasSelection() {
      if (isMultiSelect()) {
        return _.size($scope.selectedValues) > 0;
      } else {
        return !!$scope.selectedValue;
      }
    }

    /**
     * Determines whether the dropdown is configured for multiselect
     *
     * @returns {boolean} true if the dropdown is configured for multiselect
     */
    function isMultiSelect() {
      return $scope.selectType === SELECT_TYPE_MULTI;
    }

    /**
     * Determines whether a value is disabled.
     *
     * @param {object|string} value - the value to be tested
     * @returns {boolean} true if the value is disabled
     */
    function isValueDisabled(value) {
      if ($scope.disableValuesComparator) {
        return $scope.disableValuesComparator($scope.disabledValues, value);
      }
      return findIndex($scope.disabledValues, value) >= 0;
    }

    /**
     * Determines whether a value in a multiselect list is selected.
     *
     * @param {object|string} value - the value to be tested
     * @returns {boolean} true if the value is selected
     */
    function isValueSelected(value) {
      return findIndex($scope.selectedValues, value) >= 0;
    }

    /**
     * Function to prevent the drop down from closing.
     *
     * @param {Object} event - The event object triggered.
     */
    function preventClose(event) {
      // Cancel the event that would make the dropdown close.
      event.preventDefault();
      event.stopPropagation();
    }

    /**
     * Validates the settings in the scope.
     */
    function validateSettings() {
      if (UtilService.isSet($scope.defaultText)) {
        ValidationUtils.assertIsString($scope.defaultText, 'default-text');
      }

      if (UtilService.isSet($scope.disabled)) {
        ValidationUtils.assertIsBoolean($scope.disabled, 'disabled');
      }

      if (UtilService.isSet($scope.disabledValues)) {
        ValidationUtils.assertIsArray($scope.disabledValues, 'disabled-values');
      }

      if (UtilService.isSet($scope.hideSearch)) {
        ValidationUtils.assertIsBoolean($scope.hideSearch, 'hide-search');
      }

      if (UtilService.isSet($scope.labelProp)) {
        ValidationUtils.assertIsString($scope.labelProp, 'label-prop');
      }

      if (UtilService.isSet($scope.onValueSelected)) {
        ValidationUtils.assertIsFunction($scope.onValueSelected, 'on-value-selected');
      }

      if (UtilService.isSet($scope.onValueUnselected)) {
        ValidationUtils.assertIsFunction($scope.onValueUnselected, 'on-value-unselected');
      }

      if (UtilService.isSet($scope.searchPlaceholder)) {
        ValidationUtils.assertIsString($scope.searchPlaceholder, 'search-placeholder');
      }

      if (UtilService.isSet($scope.selectedValues)) {
        ValidationUtils.assertIsArray($scope.selectedValues, 'selected-values');
      }

      if (
        UtilService.isSet($scope.selectType) &&
        $scope.selectType !== SELECT_TYPE_SINGLE &&
        $scope.selectType !== SELECT_TYPE_MULTI
      ) {
        throw new Error('select-type must be "' + SELECT_TYPE_SINGLE + '" or "' + SELECT_TYPE_MULTI + '"');
      }

      ValidationUtils.assertIsArray($scope.values, 'values');

      if (_.isEmpty($scope.values)) {
        throw new Error('values must not be empty');
      }

      if (UtilService.isSet($scope.labels)) {
        ValidationUtils.assertIsArray($scope.labels, 'labels');
        if ($scope.labels.length !== $scope.values.length) {
          throw new Error(`label count ${$scope.labels.length} must equal value count ${$scope.values.length}`);
        }
      }
    }

    /**
     * Handles selection of a value
     *
     * @param {object|string} value - the selected value
     * @param {object} event - the click event
     * @param {number} index - the index in the choices list of the selected value
     */
    function valueClicked(value, event, index) {
      // Make sure the clicked value is not disabled
      if (isValueDisabled(value)) {
        $scope.preventClose(event);
        return;
      }

      if (isMultiSelect()) {
        // If the value is currently selected we're going to unselect it
        // If the value is not currently selected we're going to select it
        var isSelected = isValueSelected(value);
        if (isSelected) {
          // Unselect the value
          var selectedIndex = findIndex($scope.selectedValues, value);
          $scope.selectedValues.splice(selectedIndex, /*deleteCount*/ 1);

          if ($scope.onValueUnselected) {
            $scope.onValueUnselected({ value: value, index: index });
          }
        } else {
          // Select the value
          $scope.selectedValues.push(value);

          if ($scope.onValueSelected) {
            $scope.onValueSelected({ value: value, index: index });
          }
        }

        // Keep the menu open for more selections
        $scope.preventClose(event);
      } else {
        $scope.selectedValue = value;

        if ($scope.onValueSelected) {
          const promiseOrResult = $scope.onValueSelected({ value: value, index: index });
          // The result might be a value or a promise. To handle this possibility we'll always turn it into a promise
          $q.when(promiseOrResult).then((result) => {
            if (result === false) {
              // The callback function rejected the selection.
              $scope.selectedValue = null;
            }
          });
        }
      }
    }
  }
})();
